import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ClassRequestWithStudent, ClassRequestData } from '@/types/classRequests';
import { useToast } from '@/components/ui/use-toast';
import { useI18n } from '@/contexts/I18nContext';
import { getCurrentDateString } from '@/utils/timezoneUtils';

// Hook to get instructor pending requests count
export const useInstructorPendingRequestsCount = (instructorId: string) => {
  return useQuery({
    queryKey: ['instructor-pending-requests-count', instructorId],
    queryFn: async () => {
      // Get user's timezone (default to America/Sao_Paulo for Brazil)
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo';
      
      const { data, error } = await supabase
        .rpc('get_instructor_pending_requests_count', {
          instructor_id: instructorId,
          user_timezone: userTimezone
        });

      if (error) throw error;
      return data || 0;
    },
    enabled: !!instructorId,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

// Hook to get instructor requests with student info
export const useInstructorRequests = (instructorId: string) => {
  return useQuery({
    queryKey: ['instructor-requests', instructorId],
    queryFn: async (): Promise<ClassRequestWithStudent[]> => {
      const { data, error } = await supabase
        .rpc('get_instructor_requests_with_students', {
          instructor_id: instructorId
        });

      if (error) throw error;
      
      // Only filter out very old requests (more than 7 days old)
      const now = new Date();
      // Calculate 7 days ago in local timezone
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const sevenDaysAgoStr = getCurrentDateString(sevenDaysAgo);
      
      const filteredData = (data || []).filter(request => {
        // Only show pending requests
        if (request.status !== 'pending') return false;
        
        // TIMEZONE HANDLING:
        // requested_date is stored as DATE (no timezone) in format 'YYYY-MM-DD'
        // This represents the calendar date the user selected, regardless of timezone.
        // We compare it with the current date in the VIEWER's local timezone (not UTC).
        // This ensures:
        // - User in Brazil creates request for "2025-11-25" → stored as "2025-11-25"
        // - Instructor in USA views on "2025-11-24" (their local date) → sees request (future date)
        // - Instructor in USA views on "2025-11-25" (their local date) → sees request if time hasn't passed
        // This works correctly for users in different timezones because we compare calendar dates,
        // not timestamps. Each user sees requests based on their own local date/time.
        const today = getCurrentDateString(); // Current date in viewer's local timezone
        
        // If request is for a future date (in viewer's timezone), include it
        if (request.requested_date > today) {
          return true;
        }
        
        // If request is for today (in viewer's timezone), check if time hasn't passed
        if (request.requested_date === today) {
          const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
          // Normalize time format (remove seconds if present)
          const requestTime = request.requested_start_time.split(':').slice(0, 2).join(':');
          return requestTime > currentTime; // Compare in viewer's local time
        }
        
        // If request is for a past date (in viewer's timezone), only include if within last 7 days
        const isWithinLast7Days = request.requested_date >= sevenDaysAgoStr;
        return isWithinLast7Days;
      });
      
      return filteredData;
    },
    enabled: !!instructorId,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

// Hook to submit class request
export const useClassRequestSubmission = () => {
  const { toast } = useToast();
  const { t } = useI18n();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (requestData: ClassRequestData) => {
      const { data, error } = await supabase
        .rpc('create_class_request', {
          p_instructor_id: requestData.instructor_id,
          p_student_id: requestData.student_id,
          p_requested_date: requestData.requested_date,
          p_requested_start_time: requestData.requested_start_time,
          p_requested_end_time: requestData.requested_end_time,
          p_message: requestData.message || null
        });

      if (error) throw error;
      return data;
    },
    onSuccess: (requestId, variables) => {
      toast({
        title: t('class_request.request_submitted'),
        description: t('class_request.request_submitted_description'),
      });
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['instructor-pending-requests-count'] });
      queryClient.invalidateQueries({ queryKey: ['instructor-requests'] });
      queryClient.invalidateQueries({ queryKey: ['student-requests', variables.student_id] });
    },
    onError: (error: any) => {
      // Handle duplicate request error specifically
      if (error.code === '23505' && error.message.includes('duplicate key value violates unique constraint')) {
        toast({
          title: t('class_request.request_already_exists'),
          description: t('class_request.request_already_exists_description'),
          variant: 'destructive',
        });
      } else {
        toast({
          title: t('class_request.error'),
          description: error.message || t('class_request.failed_to_submit'),
          variant: 'destructive',
        });
      }
    },
  });
};

// Hook to approve class request
export const useApproveClassRequest = () => {
  const { toast } = useToast();
  const { t } = useI18n();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ requestId, instructorId }: { requestId: string; instructorId: string }) => {
      // Get the specific request directly from the table (check any status first)
      const { data: requestData, error: requestError } = await supabase
        .from('class_scheduling_requests')
        .select('*')
        .eq('id', requestId)
        .eq('instructor_id', instructorId)
        .single();
      
      if (requestError) {
        console.error('❌ Error fetching request:', requestError);
        throw new Error('Request not found');
      }

      if (!requestData) {
        throw new Error('Request not found');
      }

      if (requestData.status !== 'pending') {
        throw new Error(`Request is already ${requestData.status}`);
      }


      // Update request status - use the actual id from the database
      const { error: updateError } = await supabase
        .from('class_scheduling_requests')
        .update({ status: 'approved', updated_at: new Date().toISOString() })
        .eq('id', requestData.id); // Use the actual id from the table

      if (updateError) throw updateError;

      // AUTO-DENY: Deny all other pending requests for the same time slot
      const { error: denyError } = await supabase
        .from('class_scheduling_requests')
        .update({ 
          status: 'denied', 
          denied_at: new Date().toISOString(),
          updated_at: new Date().toISOString() 
        })
        .eq('instructor_id', instructorId)
        .eq('requested_date', requestData.requested_date)
        .eq('requested_start_time', requestData.requested_start_time)
        .eq('status', 'pending')
        .neq('id', requestData.id); // Don't deny the one we just approved

      if (denyError) {
        // Don't throw - approval is more important than auto-denial
      }

      // Create ONE training/class record that serves both student and instructor
      // The visualization differs based on context:
      // - Student: sees it as part of their trainings (all together, no tabs)
      // - Instructor: sees it in "My Classes" tab (via instructor_id query)
      // - Instructor "My Trainings" tab: filtered out (is_class = true)
      
      // Parse date and time components to create Date in local timezone
      // This ensures the time is interpreted as local time, not UTC
      const [year, month, day] = requestData.requested_date.split('-').map(Number);
      const [startHours, startMinutes] = requestData.requested_start_time.split(':').map(Number);
      const [endHours, endMinutes] = requestData.requested_end_time.split(':').map(Number);
      
      const startDateTime = new Date(year, month - 1, day, startHours, startMinutes, 0, 0);
      const endDateTime = new Date(year, month - 1, day, endHours, endMinutes, 0, 0);
      
      const { data: trainingData, error: trainingError } = await supabase
        .from('trainings')
        .insert({
          title: 'Private Class',
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          modality: 'Gi', // Use valid modality value
          status: 'Scheduled',
          type: 'Private Class', // Must match database function - was 'Class' before
          class_type: 'Private Class', // Required field for classes
          category: 'Private Class',
          description: 'Private class session',
          notes: requestData.message,
          duration: 60, // Default 60 minutes
          academy_id: requestData.academy_id,
          user_id: requestData.student_id, // Student who requested
          instructor_id: instructorId, // Instructor teaching (for "My Classes" tab)
          is_class: true // Mark as class (for instructor's "My Trainings" filter)
        })
        .select()
        .single();

      if (trainingError) {
        throw trainingError;
      }

      const { error: instructorError } = await supabase
        .from('training_instructors')
        .insert({
          training_id: trainingData.id,
          instructor_user_id: instructorId
        });

      if (instructorError) {
        throw instructorError;
      }

      // Create notification for student when class is approved
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert({
          user_id: requestData.student_id,
          type: 'class_approved',
          title: t('notifications.class_requests.request_approved'),
          content: t('notifications.class_requests.request_approved'),
          metadata: {
            request_id: requestData.id,
            training_id: trainingData.id,
            instructor_id: instructorId
          }
        });

      if (notificationError) {
        // Don't throw - notification is not critical, request is already approved
      }

      return { success: true, training_id: trainingData.id };
    },
    onSuccess: (data, variables) => {
      toast({
        title: t('class_request.request_approved'),
        description: t('class_request.request_approved_description'),
      });
      
      // Invalidate related queries - use prefix matching to catch all variations
      queryClient.invalidateQueries({ queryKey: ['instructor-pending-requests-count'] });
      queryClient.invalidateQueries({ queryKey: ['instructor-requests'] });
      
      // CRITICAL: Invalidate instructor-classes for ALL dates (instructor's calendar)
      // useInstructorClasses uses queryKey: ['instructor-classes', userId, 'yyyy-MM-dd']
      queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
      
      // CRITICAL: Invalidate trainings queries so Calendar refetches
      // trainings-with-notes is used by Calendar to show training cards
      // useTrainingsWithNotes uses queryKey: ['trainings-with-notes', userId, month, year]
      queryClient.invalidateQueries({ queryKey: ['trainings'] });
      queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
      queryClient.invalidateQueries({ queryKey: ['next-trainings'] });
      
      // CRITICAL: Invalidate monthly-totals so student's MonthSlider updates
      // Use prefix matching to catch all monthly-totals queries (with different user IDs and years)
      queryClient.invalidateQueries({ queryKey: ['monthly-totals'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-classes'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-trainings'] });
      
      // CRITICAL: Invalidate availability/slots queries so instructor slots page updates
      // This ensures slots are marked as unavailable after approval.
      // NOTE: useInstructorSchedule / useInstructorScheduleCounts use the key
      // ['instructor-slots', instructorId, dateStr] — must invalidate by prefix.
      queryClient.invalidateQueries({ queryKey: ['instructor-slots'] });
      queryClient.invalidateQueries({ queryKey: ['availability'] });
      queryClient.invalidateQueries({ queryKey: ['slots'] });
      queryClient.invalidateQueries({ queryKey: ['instructor-daily-totals'], exact: false });
      
      // CRITICAL: Invalidate instructor-highlights so home page statistics update
      // This ensures statistics reflect new classes and completed classes
      queryClient.invalidateQueries({ queryKey: ['instructor-highlights'] });
      
      // CRITICAL: Invalidate student statistics when class is approved
      // This ensures student home page statistics update when they get a new class
      queryClient.invalidateQueries({ queryKey: ['practitioner-highlights'] });
      queryClient.invalidateQueries({ queryKey: ['journey-highlights'] });
      
    },
    onError: (error: any) => {
      toast({
        title: t('class_request.error'),
        description: error.message || t('class_request.failed_to_approve'),
        variant: 'destructive',
      });
    },
  });
};

// Hook to deny class request
export const useDenyClassRequest = () => {
  const { toast } = useToast();
  const { t } = useI18n();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ requestId, instructorId, reason }: { requestId: string; instructorId: string; reason?: string }) => {
      const { data, error } = await supabase
        .rpc('deny_class_request', {
          p_request_id: requestId,
          p_instructor_id: instructorId,
          p_reason: reason || null
        });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: t('class_request.request_denied'),
        description: t('class_request.request_denied_description'),
      });
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['instructor-pending-requests-count'] });
      queryClient.invalidateQueries({ queryKey: ['instructor-requests'] });
      queryClient.invalidateQueries({ queryKey: ['next-trainings'] });
      // Slot count updates too (denied request frees the slot back)
      queryClient.invalidateQueries({ queryKey: ['instructor-slots'] });
    },
    onError: (error: any) => {
      toast({
        title: t('class_request.error'),
        description: error.message || t('class_request.failed_to_deny'),
        variant: 'destructive',
      });
    },
  });
};

// Hook to get student's own requests
export const useStudentRequests = (studentId: string) => {
  return useQuery({
    queryKey: ['student-requests', studentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('class_scheduling_requests')
        .select(`
          *,
          instructor:profiles!instructor_id(full_name, profile_picture_url)
        `)
        .eq('student_id', studentId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!studentId,
  });
};
